import {
  Accordion,
  AddIcon,
  Box,
  Flex,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import { useFormContext } from '@iheartjane/reefer-hook-form';
import type { GoogleFontFamily } from '@iheartjane/types';
import isEmpty from 'lodash-es/isEmpty';
import isMatchWith from 'lodash-es/isMatchWith';
import isNil from 'lodash-es/isNil';
import isObject from 'lodash-es/isObject';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { useFetchCurrentBloomMenu } from '@jane/business-admin/data-access';

import { CategoryTabsSection } from './sections/CategoryTabsSection';
import { ColorsSection } from './sections/ColorsSection';
import { FontsSection } from './sections/FontsSection';
import { LogoSection } from './sections/LogoSection';
import { ProductCardsSection } from './sections/ProductCardsSection';
import { SearchSection } from './sections/SearchSection';

// Section id should match form name so we can keep track of form edits
export type SidebarSection =
  'logo' | 'colors' | 'fonts' | 'product_cards' | 'category_tabs' | 'search';

interface SidebarSectionProps {
  activeSection: SidebarSection | null;
  allowOverflow?: boolean;
  children: ReactNode;
  id: SidebarSection;
  setActiveSection: (section: SidebarSection | null) => void;
  title: string;
}

const SidebarSection = ({
  activeSection,
  allowOverflow,
  children,
  id,
  setActiveSection,
  title,
}: SidebarSectionProps) => {
  const {
    formState: { dirtyFields },
    watch,
  } = useFormContext();

  const { data: bloomMenuData } = useFetchCurrentBloomMenu();
  const defaultTheme = bloomMenuData?.default_theme?.bloom;
  const formValues = watch(id);

  const hasDefaultValues = useMemo(() => {
    if (id === 'fonts' && defaultTheme?.fonts) {
      // Fonts can be undefined or null, check for both when comparing
      return isMatchWith(
        formValues,
        defaultTheme.fonts,
        (source, comparison) => {
          if (isNil(source) && isNil(comparison)) {
            return true;
          }
          return false;
        }
      );
    } else if (id === 'colors' && defaultTheme?.colors) {
      // Form values only has main colors so only check those when comparing
      return isMatchWith(
        formValues,
        defaultTheme.colors,
        (source, comparison) => {
          if (source?.main === comparison?.main) {
            return true;
          }
          return false;
        }
      );
    } else if (id === 'logo') {
      return formValues === undefined;
    } else {
      return;
    }
  }, [JSON.stringify(formValues), JSON.stringify(defaultTheme), id]);

  const isActive = activeSection === id;

  const hasEdits = useMemo(() => {
    return isObject(dirtyFields[id])
      ? !isEmpty(dirtyFields[id])
      : dirtyFields[id];
  }, [JSON.stringify(dirtyFields), id]);

  return (
    <Accordion.Item id={id} expanded={isActive}>
      <Box mb={16} background="grays-white" borderRadius="sm">
        <Accordion.Heading
          p={20}
          disableExpansion
          onClick={() =>
            setActiveSection(isActive ? null : (id as SidebarSection))
          }
        >
          <Flex
            gap={8}
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Typography variant="body-bold">{title}</Typography>
            <Flex alignItems="center">
              {['colors', 'fonts', 'logo'].includes(id) && hasDefaultValues && (
                <Tag
                  background="purple-light"
                  color="purple"
                  mr={12}
                  icon={
                    <AddIcon
                      style={{ width: 12, height: 12 }}
                      mr={2}
                      ml={4}
                      color="purple"
                    />
                  }
                  label="Add"
                />
              )}
              {hasEdits && (
                <Box
                  width={12}
                  height={12}
                  background="purple"
                  borderRadius="lg"
                  mr={12}
                ></Box>
              )}
              <Accordion.Indicator />
            </Flex>
          </Flex>
        </Accordion.Heading>

        <Accordion.Content px={20} allowOverflow={allowOverflow}>
          {children}
        </Accordion.Content>
      </Box>
    </Accordion.Item>
  );
};

export const EditorSidebar = ({
  activeSection,
  setActiveSection,
  supportedFontFamilies,
}: {
  activeSection: SidebarSection | null;
  setActiveSection: (section: SidebarSection | null) => void;
  supportedFontFamilies: GoogleFontFamily[];
}) => {
  const sectionProps = { activeSection, setActiveSection };

  return (
    <Flex
      flexDirection="column"
      gap={24}
      width={360}
      overflow="scroll"
      shrink={0}
    >
      <Typography variant="section-bold" as="h2">
        Menu
      </Typography>

      <Accordion>
        <SidebarSection id="logo" title="Logo" {...sectionProps}>
          <LogoSection />
        </SidebarSection>

        <SidebarSection id="colors" title="Colors" {...sectionProps}>
          <ColorsSection />
        </SidebarSection>

        <SidebarSection
          id="fonts"
          title="Fonts"
          allowOverflow
          {...sectionProps}
        >
          <FontsSection supportedFontFamilies={supportedFontFamilies} />
        </SidebarSection>

        <SidebarSection
          id="product_cards"
          title="Product cards"
          {...sectionProps}
        >
          <ProductCardsSection />
        </SidebarSection>

        <SidebarSection id="category_tabs" title="Categories" {...sectionProps}>
          <CategoryTabsSection />
        </SidebarSection>

        <SidebarSection
          id="search"
          title="Search and AI Budtender"
          {...sectionProps}
        >
          <SearchSection />
        </SidebarSection>
      </Accordion>
    </Flex>
  );
};
